import { memo, useMemo } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  LightningBoltIcon,
  SparklesIcon,
  BadgeCheckIcon,
  TrendingUpIcon,
} from "@heroicons/react/solid";

import { formatAnalyticsNumber } from "@/components/RequestsAnalytics/utils/formatAnalyticsNumber";
import { toNormalCase } from "@/utils/strings";
import { cn } from "@/lib/utils";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const StatIcon: Record<string, (props: any) => JSX.Element> = {
  averageLatency: LightningBoltIcon,
  averageScore: TrendingUpIcon,
  totalCost: CurrencyDollarIcon,
  totalRequests: ChartBarIcon,
  totalTokens: SparklesIcon,
  successRate: BadgeCheckIcon,
};

interface StatBodyProps {
  label: string;
  id: string;
  value: string;
  isLast?: boolean;
}

const StatBody = memo(({ label, id, value, isLast = false }: StatBodyProps) => {
  const Icon = StatIcon[id];
  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 items-center justify-between gap-3 px-4 py-3",
        !isLast && "border-b border-gray-200 sm:border-b-0 sm:border-r",
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 shrink-0 text-gray-400" /> : null}
        <span className="truncate text-sm text-gray-500">{label}</span>
      </div>
      <span className="shrink-0 text-sm font-semibold text-gray-900">{value}</span>
    </div>
  );
});

interface StatDataProps {
  tooltipContent?: string | null;
  id: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useStatBoxes = (stats: Record<string, any>) => {
  const normalizedStats = useMemo(() => {
    if (!stats || typeof stats !== "object") {
      return {};
    }

    return Object.entries(stats).reduce(
      (acc, [key, value]) => {
        const camelKey = key.includes("_")
          ? key.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
          : key;
        acc[camelKey] = value;
        return acc;
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {} as Record<string, any>,
    );
  }, [stats]);

  const isPendingData = useMemo(
    () =>
      (normalizedStats &&
        !Object.values(normalizedStats).find(stat => typeof stat === "number" && stat !== 0)) ||
      !normalizedStats ||
      Object.keys(normalizedStats).length === 0,
    [normalizedStats],
  );

  const statData = useMemo(() => {
    if (!normalizedStats || typeof normalizedStats !== "object") {
      return [];
    }

    return Object.keys(normalizedStats)
      .slice(0, 4)
      .map(key => {
        if (isPendingData) {
          return {
            id: key,
            label: toNormalCase(key),
            value: "-",
          };
        }
        const suffix = key === "averageLatency" ? "s" : "";

        let value = normalizedStats[key],
          tooltipContent = null;

        if (key === "totalCost") {
          value = Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
          }).format(value);
        }
        if (key === "successRate") {
          value = `${(value * 100).toFixed(2)}%`;
        }

        if (key === "totalTokens" || key === "totalRequests") {
          value = formatAnalyticsNumber(Math.round(value));
        } else if (key === "averageScore") {
          value = value as Record<string, number>;
          tooltipContent = Object.entries(value)
            .filter(score => score[0] !== "default")
            .map(([scoreName, scoreValue]) => `${scoreName}: ${Math.round(scoreValue as number)}`)
            .join(", ");
          value = Math.round(Number(value.default));
        } else if (typeof value === "number") {
          value = value.toFixed(2);
        }

        return {
          id: key,
          label: toNormalCase(key),
          value: `${value || "-"}${suffix}`,
          tooltipContent,
        };
      });
  }, [isPendingData, normalizedStats]);

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white sm:flex-row">
      {statData?.map(({ id, label, value, tooltipContent }: StatDataProps, index: number) => {
        const isLast = index === statData.length - 1;
        if (tooltipContent && id === "averageScore") {
          return (
            <TooltipPrimitive.Provider key={id} delayDuration={100}>
              <TooltipPrimitive.Root>
                <TooltipPrimitive.Trigger asChild>
                  <div className="flex min-w-0 flex-1">
                    <StatBody label={label} id={id} value={value} isLast={isLast} />
                  </div>
                </TooltipPrimitive.Trigger>
                <TooltipPrimitive.Portal>
                  <TooltipPrimitive.Content>
                    <div className="max-h-36 max-w-lg overflow-scroll rounded-md bg-white p-8 shadow-md">
                      <div className="mb-2 border-b border-gray-200 pb-2 text-xs font-semibold uppercase tracking-wider text-gray-800">
                        Other average scores
                      </div>
                      {tooltipContent.split(", ").map((score: string, scoreIndex: number) => {
                        const [scoreName, scoreValue] = score.split(": ");
                        return (
                          <div
                            // eslint-disable-next-line react/no-array-index-key
                            key={scoreIndex}
                            className="font-mono text-sm text-gray-700"
                          >
                            {`${scoreName}: ${scoreValue}`}
                          </div>
                        );
                      })}
                    </div>
                  </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
              </TooltipPrimitive.Root>
            </TooltipPrimitive.Provider>
          );
        }
        return <StatBody key={id} label={label} id={id} value={value} isLast={isLast} />;
      })}
    </div>
  );
};
